/**
 * The TV chrome.
 *
 * A left sidebar rather than the web layout's top bar: a remote moves in straight lines,
 * and a vertical menu means Left from any content reaches navigation and Right returns,
 * with no diagonal moves to guess at. The same menu across the top would put every
 * section behind an Up press from wherever you happen to be in a grid.
 *
 * This is a renderer, not a second app: it consumes the same hooks, the same session
 * store and the same API client as the web layout. Only the presentation differs.
 */
import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { FocusProvider, useSpatialNavigation } from './focus/FocusContext';
import { AutoFocusables } from './focus/AutoFocusables';
import { useFocusable } from './focus/useFocusable';
import { useBackStack } from './useBackStack';
import { useSession } from '../../store/session';
import { Avatar } from '../../components/Avatar';
import { MediaImage } from '../../components/MediaImage';

const SECTIONS = [
  { to: '/home', label: 'Ana Sayfa' },
  // Second, and on a television that placement earns itself: finding one title by moving
  // through rails costs dozens of presses, and this costs one plus a sentence.
  { to: '/search', label: 'Ara' },
  { to: '/live', label: 'Canlı TV' },
  { to: '/movies', label: 'Filmler' },
  { to: '/series', label: 'Diziler' },
  { to: '/mylist', label: 'Listem' },
  { to: '/profiles', label: 'Profil' },
] as const;

function NavItem({ to, label, active }: { to: string; label: string; active: boolean }) {
  const navigate = useNavigate();
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id: `nav:${to}`,
    group: 'nav',
    onSelect: () => void navigate(to),
  });

  return (
    <button
      ref={ref}
      type="button"
      className={`tvnav__item${focused ? ' is-focused' : ''}${active ? ' is-active' : ''}`}
      onClick={() => navigate(to)}
    >
      {label}
    </button>
  );
}

function Chrome({ children, bare }: { children: ReactNode; bare?: boolean }) {
  const location = useLocation();
  const { profile } = useSession();
  const onBack = useBackStack();

  // Mounted once, here, so exactly one listener owns the remote.
  useSpatialNavigation(onBack);

  // The player is full-bleed; a sidebar over it would cover picture and steal focus.
  const hideNav = bare || location.pathname.startsWith('/watch/');
  if (hideNav) {
    return (
      <div className="tv tv--bare">
        <AutoFocusables>{children}</AutoFocusables>
      </div>
    );
  }

  /*
   * The same class the web shell sets, on the same tokens.
   *
   * What it changes on the panel is the palette and the corner radius, and deliberately not
   * the card geometry — `styles/patterns/kids.css` explains why 1.4× cards would break
   * spatial navigation here while solving a problem the television does not have.
   */
  const kids = Boolean(profile?.isKid);

  return (
    <div className={`tv${kids ? ' kids' : ''}`}>
      <nav className="tvnav" aria-label="Bölümler">
        <div className="tvnav__profile">
          {profile && <Avatar profile={profile} size={56} />}
          <span>{profile?.name ?? ''}</span>
        </div>
        {SECTIONS.map((s) => (
          <NavItem
            key={s.to}
            to={s.to}
            label={s.label}
            active={location.pathname.startsWith(s.to)}
          />
        ))}
      </nav>
      <main className="tv__content">
        {/* Wraps every screen, not only the ones written for the TV: the purpose-built
            ones mark their own elements and are skipped, and the rest become navigable
            without a second implementation. See AutoFocusables for why. */}
        <AutoFocusables>{children}</AutoFocusables>
      </main>
    </div>
  );
}

export function TvShell({ children, bare }: { children: ReactNode; bare?: boolean }) {
  return (
    <FocusProvider>
      <Chrome bare={bare}>{children}</Chrome>
    </FocusProvider>
  );
}

/**
 * A focusable catalog tile.
 *
 * Exported from here rather than components/ui because its focus treatment is the whole
 * point and it has no meaning outside a TV layout.
 *
 * Focus is `transform: scale()` plus an outline — never box-shadow or a blur animation.
 * On 2020-era Tizen GPUs those composite off the fast path and a rail of twenty cards
 * drops frames while scrolling, which is exactly when it is most visible.
 */
export function TvCard({
  id,
  group,
  title,
  subtitle,
  art,
  progressPercent,
  poster,
  onSelect,
}: {
  id: string;
  group?: string;
  title: string;
  subtitle?: string | null;
  art?: string | null;
  progressPercent?: number;
  /** 2:3 artwork instead of 16:9 — right for films and series, wrong for channel logos. */
  poster?: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLDivElement>({ id, group, onSelect });

  return (
    <div
      ref={ref}
      className={`tvcard${poster ? ' tvcard--poster' : ''}${focused ? ' is-focused' : ''}`}
      role="button"
      aria-label={title}
      onClick={onSelect}
    >
      <div className="tvcard__art">
        {/*
          Seeded on the title, not on `id`. `id` here is the focus registry key and is
          deliberately positional (`lib:row:2:4`) so the same film appearing in two rows
          stays reachable in both — seeding a colour from it would give one film two
          colours and change them as rows reorder. The title is the stable thing a TvCard
          is given.
        */}
        <MediaImage src={art} shape={poster ? 'poster' : 'wide'} fallback={title} seed={title} />
        {progressPercent != null && progressPercent > 0 && (
          <div className="tvcard__bar">
            <span style={{ width: `${Math.min(100, progressPercent)}%` }} />
          </div>
        )}
      </div>
      <span className="tvcard__title">{title}</span>
      {subtitle && <span className="tvcard__meta">{subtitle}</span>}
    </div>
  );
}

/** A horizontal rail. Windowed by the caller; this only lays out and labels. */
export function TvRail({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tvrail">
      <h2 className="tvrail__title">{title}</h2>
      <div className="tvrail__track">{children}</div>
    </section>
  );
}

/** Convenience for screens that need Back to do something other than navigate. */
export { useBackHandler } from './useBackStack';

export function useTvNavigate() {
  const navigate = useNavigate();
  return useCallback((to: string) => navigate(to), [navigate]);
}
